import type { InternalScenarioSchedule } from '../components/Scenario';

// Use dynamic import for localForage to prevent SSR issues
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let localForagePromise: Promise<any> | null = null;

// Mock localForage interface for SSR
interface LocalForageInterface {
  _driver: string;
  defineDriver: () => Promise<void>;
  setDriver: () => Promise<void>;
  getItem: <T>(key: string) => Promise<T | null>;
  setItem: <T>(key: string, value: T) => Promise<T>;
  removeItem: (key: string) => Promise<void>;
  clear: () => Promise<void>;
  length: () => Promise<number>;
  key: (index: number) => Promise<string>;
  keys: () => Promise<string[]>;
  iterate: <T, U>(
    iteratee: (value: T, key: string, iterationNumber: number) => U,
  ) => Promise<U>;
}

const getLocalForage = (): Promise<LocalForageInterface> => {
  if (typeof window === 'undefined') {
    // Return null driver for SSR environment
    return Promise.resolve({
      _driver: 'NullDriver',
      defineDriver: () => Promise.resolve(),
      setDriver: () => Promise.resolve(),
      getItem: () => Promise.resolve(null),
      setItem: <T>(_key: string, value: T) => Promise.resolve(value),
      removeItem: () => Promise.resolve(),
      clear: () => Promise.resolve(),
      length: () => Promise.resolve(0),
      key: () => Promise.resolve(''),
      keys: () => Promise.resolve([]),
      iterate: () => Promise.resolve(null),
    } as LocalForageInterface);
  }

  if (!localForagePromise) {
    localForagePromise = import('localforage').then((module) => {
      return module.default;
    });
  }
  return localForagePromise;
};

const cleanedLocalForage = (() => {
  if (typeof window === 'undefined') return () => Promise.resolve();
  // Increase if the schema stored into localForage is changed so any client will purge the old schema on init.
  const CURRENT_FORAGE_VERSION = 11.5;
  const purgeOldForageVersion = async () => {
    const localForage = await getLocalForage();
    const savedVersion: number | null = await localForage.getItem('version');
    if (CURRENT_FORAGE_VERSION !== savedVersion) {
      await localForage.clear();
      await localForage.setItem('version', CURRENT_FORAGE_VERSION);
    }
  };

  let localForageCleanedOnce: Promise<void> | null = null;

  return () => {
    if (localForageCleanedOnce === null) {
      localForageCleanedOnce = purgeOldForageVersion().catch((err) => {
        console.warn(
          `Could not clean local forage: "${err.message}". If not running in a browser this is expected.`,
        );
      });
    }
    return localForageCleanedOnce;
  };
})();

interface SavedItemTypes {
  scenarios: InternalScenarioSchedule[];
  manualBunkerPrice: number | null;
}

export const readStore = async <K extends keyof SavedItemTypes>(
  key: K,
): Promise<SavedItemTypes[K] | null> => {
  try {
    await cleanedLocalForage();
    const localForage = await getLocalForage();
    const fromStore = await localForage.getItem<SavedItemTypes[K]>(key);
    return fromStore ?? null;
  } catch (_err) {
    console.log('localForage (getItem) - Error', _err);
    return null;
  }
};

export const writeStore = async <K extends keyof SavedItemTypes>(
  key: K,
  value: SavedItemTypes[K],
): Promise<void> => {
  try {
    await cleanedLocalForage();
    const localForage = await getLocalForage();
    await localForage.setItem(key, value);
  } catch (_err) {
    console.log('localForage (setItem) - Error', _err);
  }
};

export const resetStore = async (): Promise<void> => {
  try {
    const localForage = await getLocalForage();
    await localForage.clear();
  } catch (_err) {
    console.log('localForage (clear) - Error', _err);
  }
};
